import { ALEXANDRIA_METRICS } from 'dashboard409a/services/alexandria/types';

// Fixed-order categorical palette for per-company series. Validated for CVD
// separation and lightness/chroma bands against the light surface (dataviz
// checks); identity is never color-alone — every chart ships a legend and the
// performance table is the text fallback. Do not cycle or shuffle this order.
export const SERIES_COLORS = [
  '#1A64B1',
  '#00A58C',
  '#B25E00',
  '#52A3FF',
  '#C0399F',
  '#0E8A72',
  '#D99000',
  '#E05B5B',
];

// Gray is reserved for the all-companies median reference line, never a series.
export const MEDIAN_COLOR = '#6E6E6E';

// Median-of-all-comps line in the Multiples chart.
export const MULTIPLE_MEDIAN_COLOR = '#3BCE8E';

export const COHORT_COLORS = {
  median: MEDIAN_COLOR,
  high: '#E05B5B',
  medium: '#D99000',
  low: '#52A3FF',
} as const;

export const COHORT_LABELS = {
  high: 'High Growth (≥ 40%)',
  medium: 'Medium Growth (20% - 40%)',
  low: 'Low Growth (≤ 20%)',
} as const;

export interface MultipleOption {
  key: string;
  label: string;
  denominator: string;
}

export const MULTIPLE_OPTIONS: MultipleOption[] = [
  { key: 'ev_ntm_revenue', label: 'EV/NTM Revenue', denominator: ALEXANDRIA_METRICS.NTM_REVENUE },
  { key: 'ev_ltm_revenue', label: 'EV/LTM Revenue', denominator: ALEXANDRIA_METRICS.LTM_REVENUE },
  { key: 'ev_ntm_ebitda', label: 'EV/NTM EBITDA', denominator: ALEXANDRIA_METRICS.NTM_EBITDA },
  { key: 'ev_ltm_ebitda', label: 'EV/LTM EBITDA', denominator: ALEXANDRIA_METRICS.LTM_EBITDA },
];

export interface TimeRangeOption {
  key: string;
  days: number;
  // Grid density requested from Alexandria — coarser for long windows to keep
  // payloads bounded.
  intervalDays: number;
}

// Keys are emitted by RangeFilter. 'ytd' computes its start date and adaptive
// interval in rangeToDates; its days/interval values here are unused.
export const TIME_RANGES: TimeRangeOption[] = [
  { key: 'month_3', days: 91, intervalDays: 1 },
  { key: 'month_6', days: 182, intervalDays: 1 },
  { key: 'ytd', days: 0, intervalDays: 1 },
  { key: 'year_1', days: 365, intervalDays: 3 },
  { key: 'year_3', days: 1095, intervalDays: 7 },
  { key: 'year_5', days: 1825, intervalDays: 14 },
  { key: 'year_10', days: 3650, intervalDays: 30 },
];

export const DEFAULT_RANGE_KEY = 'month_3';

// Values calculated from Alexandria series instead of requested directly.
export const COMPUTED_METRICS = {
  NTM_REVENUE_GROWTH: 'ntm_revenue_growth',
} as const;

// Every metric the cards, bars, and table consume — fetched once for the
// default window.
export const COMPS_METRICS: string[] = [
  ALEXANDRIA_METRICS.ENTERPRISE_VALUE,
  ALEXANDRIA_METRICS.LTM_REVENUE,
  ALEXANDRIA_METRICS.NTM_REVENUE,
  ALEXANDRIA_METRICS.LTM_EBITDA,
  ALEXANDRIA_METRICS.NTM_EBITDA,
  ALEXANDRIA_METRICS.GROSS_MARGIN,
  ALEXANDRIA_METRICS.EBITDA_MARGIN,
  ALEXANDRIA_METRICS.LTM_REVENUE_GROWTH,
];

// The time-series charts refetch on every range switch, so they request only
// what they plot. NTM revenue growth is derived from the requested NTM/LTM
// revenue series using Scalar's established formula.
export const CHART_METRICS: string[] = [
  ALEXANDRIA_METRICS.ENTERPRISE_VALUE,
  ALEXANDRIA_METRICS.LTM_REVENUE,
  ALEXANDRIA_METRICS.NTM_REVENUE,
  ALEXANDRIA_METRICS.LTM_EBITDA,
  ALEXANDRIA_METRICS.NTM_EBITDA,
];

export interface CompRef {
  // Alexandria ticker used as the stable UI/series key (for example "NYSE:CRM").
  ticker: string;
  label: string;
  // Set only when ticker contains a direct CapIQ company ID rather than a
  // ticker symbol. Both are sent through the same `tickers` request argument.
  companyId?: string;
}
